import {getArchiveItemName} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {assert, assertAndContinue} from 'Asserts';
import {InventoryFeature, ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {
  SchemaInterfaceExtendingSchema,
  TagSchema,
  TagSchemaSupportedFolderItemType,
} from 'constants/RegistryConstants';
import {
  BenchlingTypeDef,
  isBenchlingTypeDefAny,
  isSubType,
  LeafType,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexType} from 'data-types/benchling-type-def/ComplexTypes';
import {LEAF_TYPE_TO_ICON} from 'data-types/benchling-type-def/leafTypeToIcon';
import {getItemTypeDefForSchema} from 'data-types/benchling-type-def/ModelToTypeDef';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DEFAULT_ENTITY_SCHEMA_OPTION_NAME} from 'notebook/structured-tables/lookup-columns//NotebookLookupColumnUtils';
import {GridSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import isSchemaFieldArchived, {getSchemaFieldArchivableItem} from 'schemas/fields/isSchemaFieldArchived';
import {
  FieldType,
  getRequiredLinkSchemaId,
  getRequiredLinkStorableSchema,
  isRequiredLinkStaticSchemaInterface,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {BaseBioentityType} from 'schemas/schema-interface/SchemaInterfaceBaseBioentityTypeDropdown';
import {Schema} from 'schemas/SchemaConstants';
import {isSchemaInterface} from 'schemas/SchemaPredicates';
import {isStudyableLeafTypeDef} from 'studies/studyable/StudyableUtils';
import {getTagSchemaTypeName} from 'tag-schema/util/TagSchemaUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {assertNever, MakeFieldsOptional} from 'util/Types';

import {ProcessTaskSchemaGraphQL} from '../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {SchemaInterfaceWithFieldsGraphQL} from '../../graphql-fragments/schemas/schema-interfaces/__generated/SchemaInterfaceWithFieldsGraphQL';
import {
  getAllProcessTaskSchemasFromCache,
  getAllResultSchemasFromCache,
  getAssaySchemaFromCache,
  getBoxSchemaFromCache,
  getContainerSchemaFromCache,
  getLocationSchemaFromCache,
  getPlateSchemaFromCache,
  getProcessTaskSchemaFromCache,
  getStorableSchemaFromCache,
  getTagSchemaOrSchemaInterfaceFromCache,
} from './AllSchemasQueries';
import {InferredLookupStepType, inferTypeFromNonModifierStep} from './inferTypeFromLookupConfig';
import {LookupConfigModalPropsWithoutExtraState} from './LookupConfigModal';
import {
  ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES,
  ContainerByGridPositionLookupStepSpec,
  FilterLookupStepSpec,
  FilterType,
  LookupStepInfo,
  LookupStepSpec,
  LookupStepType,
  MODIFIER_LOOKUP_STEP_TYPES,
  NotebookColumnItemType,
  NUMERIC_FILTER_TYPES,
  ReplicatesLookupStepSpec,
  SchemaFieldLookupStepSpec,
  TypecastableLookupStepSpec,
} from './LookupConfigModalConstants';

const FIELD_STEP_TYPES = [LookupStepType.SCHEMA_FIELD, LookupStepType.FILTER];
export const DETAIL_STEP_TYPES = [
  LookupStepType.ALIASES,
  LookupStepType.AVAILABLE_SPACE,
  LookupStepType.BARCODE,
  LookupStepType.CHEMICAL_STRUCTURE,
  LookupStepType.COMPUTED_EXPIRATION_DATE,
  LookupStepType.CONCENTRATION,
  LookupStepType.COUNT,
  LookupStepType.FREEZE_THAW_COUNT,
  LookupStepType.LOCATION_PATH,
  LookupStepType.NAME,
  LookupStepType.QUANTITY,
  LookupStepType.REGISTRY_IDENTIFIER,
  LookupStepType.RESIDUES,
  LookupStepType.WELL_COORDINATES,
  LookupStepType.STUDIES,
];

export function isFieldStep(
  lookupStep: LookupStepSpec
): lookupStep is SchemaFieldLookupStepSpec | FilterLookupStepSpec {
  return FIELD_STEP_TYPES.includes(lookupStep.type);
}

// Eventually other steps could be made compatible with typecasting like NOTEBOK_COLUMN_TYPECHECK
export function isStepWithTypecastSchemaMetadata(
  lookupStep: LookupStepSpec
): lookupStep is LookupStepSpec & TypecastableLookupStepSpec {
  return lookupStep.type === LookupStepType.SCHEMA_FIELD;
}

function isSourceOrDestinationStep(lookupStepInfo: LookupStepInfo): boolean {
  return (
    lookupStepInfo.lookupStep.type === LookupStepType.SOURCE ||
    lookupStepInfo.lookupStep.type === LookupStepType.DESTINATION
  );
}

export function hasLookupStepContext(
  lookupStepInfo: LookupStepInfo
): lookupStepInfo is LookupStepInfo & {lookupStepContext: Array<LookupStepInfo>} {
  return lookupStepInfo.lookupStepContext != null;
}

export function getLookupStepInfoWithType(
  lookupStepInfo: LookupStepInfo | undefined
): LookupStepInfo | undefined {
  if (lookupStepInfo != null && hasLookupStepContext(lookupStepInfo)) {
    const itemWithType = getStepItemWithSourceDestinationType(lookupStepInfo);
    return itemWithType;
  }
  return lookupStepInfo;
}

export function getStepItemWithSourceDestinationType(
  lookupStepInfo: LookupStepInfo & {lookupStepContext: Array<LookupStepInfo>}
): LookupStepInfo | undefined {
  const lookupStepInfos = lookupStepInfo.lookupStepContext;
  let i = lookupStepInfos.length - 1;
  let itemWithType = lookupStepInfos[i];
  while (itemWithType && MODIFIER_LOOKUP_STEP_TYPES.includes(itemWithType.lookupStep.type)) {
    i -= 1;
    itemWithType = lookupStepInfos[i];
  }
  return itemWithType;
}

function _canFieldBeStorableOfType(schemaField: SchemaField, storableType: StorableItemType): boolean {
  if (schemaField.fieldType !== FieldType.STORABLE_LINK) {
    return false;
  }
  const storableSchema = getRequiredLinkStorableSchema(schemaField);
  if (storableSchema == null) {
    return true;
  }
  switch (storableType) {
    case ModelTypeConstants.CONTAINER:
      return APIUtil.isContainerSchema(storableSchema.id);
    case ModelTypeConstants.PLATE:
      return APIUtil.isPlateSchema(storableSchema.id);
    case ModelTypeConstants.BOX:
      return APIUtil.isBoxSchema(storableSchema.id);
    case ModelTypeConstants.LOCATION:
      return APIUtil.isLocationSchema(storableSchema.id);
    default:
      assert(false, 'Unexpected storable type');
      return null as never;
  }
}

export function canFieldBeBox(schemaField: SchemaField): boolean {
  return _canFieldBeStorableOfType(schemaField, ModelTypeConstants.BOX);
}
export function canFieldBeContainer(schemaField: SchemaField): boolean {
  return _canFieldBeStorableOfType(schemaField, ModelTypeConstants.CONTAINER);
}
export function canFieldBePlate(schemaField: SchemaField): boolean {
  return _canFieldBeStorableOfType(schemaField, ModelTypeConstants.PLATE);
}

export function getPlateOrBoxContainerLabel(
  schemaField: SchemaField,
  wellOrContainerLabel: string,
  storableSchema: Schema | null
): string {
  if (canFieldBePlate(schemaField) && storableSchema != null) {
    const plateSchemaType = (storableSchema as GridSchema)?.plateSchemaType;
    return plateSchemaType === PlateSchemaType.FIXED_PLATE
      ? 'Well'
      : plateSchemaType === PlateSchemaType.MATRIX_PLATE
        ? 'Container'
        : wellOrContainerLabel;
  }
  return canFieldBePlate(schemaField) ? wellOrContainerLabel : 'Container';
}

export function getIconForLookupStepInfo(lookupStepInfo: LookupStepInfo): BenchlingIconName {
  const stepType = lookupStepInfo.lookupStep.type;
  switch (stepType) {
    case LookupStepType.REPLICATES:
      return 'number';
    case LookupStepType.FILTER:
    case LookupStepType.FILTER_BY_ORIGIN:
      return 'filter';
    case LookupStepType.LATEST:
      return 'filter-menu';
    case LookupStepType.SAMPLE_OWNERS:
    case LookupStepType.AUTHORIZED_USERS:
      return 'team';
    case LookupStepType.SOURCE:
    case LookupStepType.DESTINATION:
      if (hasLookupStepContext(lookupStepInfo)) {
        const itemWithType = getStepItemWithSourceDestinationType(lookupStepInfo);
        if (itemWithType) {
          return getIconForLookupStepInfo(itemWithType);
        }
      }
      return 'unknown';
    case LookupStepType.FLOWCHART_PROCESS_TASK:
      if (BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG) {
        return 'start-node';
      }
      return 'process-task';
    case LookupStepType.STUDIES:
      return 'study';
    default: {
      const inferredType = inferTypeFromLookupStepInfo(lookupStepInfo);
      return inferredType &&
        !(inferredType.typeDef instanceof ComplexType) &&
        !isBenchlingTypeDefAny(inferredType.typeDef)
        ? LEAF_TYPE_TO_ICON[inferredType.typeDef]
        : 'unknown';
    }
  }
}

/**
 * Whether the "Stored within" inventory filter should be offered for the given preceding lookup step.
 *
 * This is gated behind:
 * - `allowInventoryFilters`: only surfaces that opt in (currently notebook structured tables) enable it.
 *   Note the backend does not impose this surface restriction; it is enforced purely on the frontend.
 * - the `INVENTORY_ENABLE_LOOKUP_FILTER_BY_STORED_WITHIN` config flag.
 * - the preceding step resolving to a storable type (only storables can be "stored within" another storable).
 */
export function supportsInventoryFilters(
  parentLookupStepInfo: LookupStepInfo | null | undefined,
  allowInventoryFilters: boolean | undefined
): boolean {
  if (!allowInventoryFilters || !BenchlingConfig.INVENTORY_ENABLE_LOOKUP_FILTER_BY_STORED_WITHIN) {
    return false;
  }
  if (parentLookupStepInfo == null) {
    return false;
  }
  const inferredType = inferTypeFromLookupStepInfo(parentLookupStepInfo);
  return inferredType != null && isSubType(inferredType.typeDef, LeafType.STORABLE);
}

export const SUPPORTED_FILTER_TYPES = [
  FilterType.EQ,
  FilterType.NE,
  FilterType.LT,
  FilterType.LE,
  FilterType.GE,
  FilterType.GT,
  FilterType.ISNULL,
  FilterType.NOTNULL,
];

export function filterOptionsForFieldType(fieldType: FieldType): Array<FilterType> {
  switch (fieldType) {
    case FieldType.TEXT:
    case FieldType.LONG_TEXT:
    case FieldType.SELECTOR:
      return SUPPORTED_FILTER_TYPES.filter((ft: FilterType) => !NUMERIC_FILTER_TYPES.includes(ft));
    case FieldType.INTEGER:
    case FieldType.FLOAT:
      return SUPPORTED_FILTER_TYPES;
    default:
      return [FilterType.ISNULL, FilterType.NOTNULL];
  }
}

export function getSchemaFieldsForLookupStep(
  schema: Schema,
  excludeNonLinkFields: boolean,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean
): Array<SchemaField> {
  // TODO(BNCH-120046): Add support for static schema interface link fields
  const fields = schema.fields.filter(
    (field) =>
      FieldType.BATCH_LINK !== field.fieldType && !isRequiredLinkStaticSchemaInterface(field.requiredLink)
  );
  if (schemaFieldFilterFn) {
    return fields.filter((field) => schemaFieldFilterFn(field, excludeNonLinkFields));
  }
  return fields;
}

function stepHasBoxSchema(data: LookupStepSpec): data is LookupStepSpec & {boxSchema: string} {
  return data.hasOwnProperty('boxSchema') && (data as LookupStepSpec & {boxSchema: string}).boxSchema != null;
}

function stepHasContainerSchema(data: LookupStepSpec): data is LookupStepSpec & {containerSchema: string} {
  return (
    data.hasOwnProperty('containerSchema') &&
    (data as LookupStepSpec & {containerSchema: string}).containerSchema != null
  );
}

function stepHasEntitySchema(data: LookupStepSpec): data is LookupStepSpec & {entitySchema: string} {
  return (
    data.hasOwnProperty('entitySchema') &&
    (data as LookupStepSpec & {entitySchema: string}).entitySchema != null
  );
}

function stepHasLocationSchema(data: LookupStepSpec): data is LookupStepSpec & {locationSchema: string} {
  return (
    data.hasOwnProperty('locationSchema') &&
    (data as LookupStepSpec & {locationSchema: string}).locationSchema != null
  );
}

function stepHasPlateSchema(data: LookupStepSpec): data is LookupStepSpec & {plateSchema: string} {
  return (
    data.hasOwnProperty('plateSchema') && (data as LookupStepSpec & {plateSchema: string}).plateSchema != null
  );
}

export function inferTypeFromLookupStepInfo(lookupStepInfo: LookupStepInfo): InferredLookupStepType | null {
  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo) ?? lookupStepInfo;
  const lookupStep = lookupStepInfoWithType.lookupStep;
  if (
    [...MODIFIER_LOOKUP_STEP_TYPES, LookupStepType.SOURCE, LookupStepType.DESTINATION].includes(
      lookupStep.type
    )
  ) {
    return null;
  }
  if (
    lookupStep.type === LookupStepType.SCHEMA_FIELD ||
    lookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION
  ) {
    if (lookupStep.schemaField == null || lookupStepInfoWithType.schemaField == null) {
      return null;
    }
  }

  return inferTypeFromNonModifierStep(
    lookupStep,
    lookupStepInfoWithType.parentSchema,
    lookupStepInfoWithType.linkSchema
  );
}

export function getLookupStepSchemaId(lookupStepInfo: LookupStepInfo): string | null {
  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
  const lookupStep = lookupStepInfoWithType ? lookupStepInfoWithType.lookupStep : lookupStepInfo.lookupStep;

  if (lookupStep.type === LookupStepType.NOTEBOOK_COLUMN_TYPECHECK) {
    switch (lookupStep.notebookColumnItemType) {
      case NotebookColumnItemType.CONTAINER:
      case NotebookColumnItemType.BOX:
      case NotebookColumnItemType.PLATE:
      case NotebookColumnItemType.LOCATION:
      case NotebookColumnItemType.STORABLE:
        return lookupStep.notebookColumnStorableSchema ?? null;
      case NotebookColumnItemType.ENTITY:
        return lookupStep.notebookColumnEntitySchema ?? null;
      default: {
        assertAndContinue(
          false,
          `Unexpected Notebook lookup item type ${assertNever(lookupStep.notebookColumnItemType)}`
        );
        return null;
      }
    }
  }

  if (stepHasBoxSchema(lookupStep)) {
    return lookupStep.boxSchema;
  }
  if (stepHasContainerSchema(lookupStep)) {
    return lookupStep.containerSchema;
  }
  if (stepHasEntitySchema(lookupStep)) {
    return lookupStep.entitySchema;
  }
  if (stepHasLocationSchema(lookupStep)) {
    return lookupStep.locationSchema;
  }
  if (stepHasPlateSchema(lookupStep)) {
    return lookupStep.plateSchema;
  }
  if (lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK) {
    return lookupStep.processTaskSchema;
  }

  return lookupStepInfoWithType?.linkSchema?.id ?? null;
}

export function getObjectTypesForLookupStepInfo(lookupStepInfo: LookupStepInfo): {
  isAaSequenceStep: boolean;
  isBoxStep: boolean;
  isBoxContainerStep: boolean;
  isContainerStep: boolean;
  isPlateStep: boolean;
  isPlateContainerStep: boolean;
  isEntityStep: boolean;
  isResiduesSupportedStep: boolean;
  isChemicalStructureSupportedStep: boolean;
  isPlateLayoutStep: boolean;
  isProcessTaskStep: boolean;
  isLocationStep: boolean;
  isSequenceStep: boolean;
  isStudyableStep: boolean;
} {
  const isInferredType = (...potentialTypes: Array<BenchlingTypeDef>): boolean =>
    inferredType?.typeDef ? potentialTypes.includes(inferredType?.typeDef) : false;

  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
  const lookupStep = lookupStepInfoWithType ? lookupStepInfoWithType.lookupStep : lookupStepInfo.lookupStep;

  const inferredType = inferTypeFromLookupStepInfo(lookupStepInfo);
  const isAaSequenceStep = inferredType
    ? isInferredType(LeafType.PROTEIN)
      ? true
      : isInferredType(LeafType.BIOENTITY) &&
        !(lookupStepInfo.linkSchema && isSchemaInterface(lookupStepInfo.linkSchema))
    : false;
  const isSequenceStep = inferredType
    ? isInferredType(LeafType.SEQUENCE)
      ? true
      : isInferredType(LeafType.BIOENTITY) &&
        !(lookupStepInfo.linkSchema && isSchemaInterface(lookupStepInfo.linkSchema))
    : false;
  const isBoxStep = inferredType ? isInferredType(LeafType.STORABLE, LeafType.BOX) : false;
  let isContainerStep = inferredType
    ? isInferredType(LeafType.STORABLE, LeafType.CONTAINER) && !lookupStepInfo.storableLookupMustBeParent
    : false;
  const isPlateStep = inferredType ? isInferredType(LeafType.STORABLE, LeafType.PLATE) : false;
  const isLocationStep = inferredType ? isInferredType(LeafType.STORABLE, LeafType.LOCATION) : false;
  const isEntityStep = inferredType ? isSubType(inferredType.typeDef, LeafType.BIOENTITY) : false;
  const isResiduesSupportedStep = inferredType
    ? isInferredType(LeafType.SEQUENCE, LeafType.PROTEIN)
      ? true
      : isInferredType(LeafType.BIOENTITY) &&
        !(lookupStepInfo.linkSchema && isSchemaInterface(lookupStepInfo.linkSchema))
    : false;
  const isChemicalStructureSupportedStep = inferredType ? isInferredType(LeafType.MOLECULE) : false;
  const isProcessTaskStep = lookupStep.type === LookupStepType.PROCESS_TASK;

  let isPlateContainerStep = isContainerStep && lookupStep.type !== LookupStepType.BOX_CONTAINERS;
  let isBoxContainerStep = isContainerStep && lookupStep.type !== LookupStepType.WELLS;
  if (lookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION && lookupStepInfo.schemaField) {
    isBoxContainerStep = canFieldBeBox(lookupStepInfo.schemaField);
    isPlateContainerStep = canFieldBePlate(lookupStepInfo.schemaField);
  }

  // if a plate layout is defined and the current step is using the source or destination plate,
  // the plate step hides the subsequent container step
  const isSourceDestinationStep = isSourceOrDestinationStep(lookupStepInfo);
  const isPlateLayoutStep = Boolean(lookupStepInfo.hasPlateLayout) && isPlateStep && isSourceDestinationStep;
  if (isPlateLayoutStep) {
    isPlateContainerStep = true;
    isContainerStep = true;
  }
  const isStudyableStep = isStudyableLookupStep(inferredType);

  return {
    isAaSequenceStep,
    isBoxStep,
    isBoxContainerStep,
    isContainerStep,
    isPlateStep,
    isPlateContainerStep,
    isEntityStep,
    isLocationStep,
    isResiduesSupportedStep,
    isChemicalStructureSupportedStep,
    isPlateLayoutStep,
    isProcessTaskStep,
    isSequenceStep,
    isStudyableStep,
  };
}

function isStudyableLookupStep(inferredType: InferredLookupStepType | null): boolean {
  if (!BenchlingConfig.STUDIES_ENABLE) {
    return false;
  }
  if (inferredType == null) {
    return false;
  }

  // In some cases, we may have a leaf type such as LeafType.BIOENTITY, which is an abstract parent type.
  // Some concrete types under this parent may be studyable (e.g., BasicFolderItem), while others may not be (e.g., Mixture).
  // If a specific link schema is provided, we can resolve its leaf type to more accurately determine
  // whether the step is studyable. Otherwise, we fall back to using the inferred type, which may be abstract.
  // We enable the studies lookup cell for abstract parent types and allow the lookup cell to display a warning
  // if the selected lookup type is not studyable.
  const resolvedTypeDef = inferredType.linkSchema
    ? getItemTypeDefForSchema(inferredType.linkSchema)
    : inferredType.typeDef;

  return isStudyableLeafTypeDef(resolvedTypeDef, {includeAbstractParentTypes: true});
}

export function getFollowingStepTypes(
  lookupStepInfo: LookupStepInfo,
  allowedLookupStepTypes: Array<LookupStepType>
): Array<LookupStepType> {
  const {
    isAaSequenceStep,
    isBoxStep,
    isBoxContainerStep,
    isContainerStep,
    isPlateStep,
    isPlateContainerStep,
    isEntityStep,
    isLocationStep,
    isProcessTaskStep,
    isSequenceStep,
  } = getObjectTypesForLookupStepInfo(lookupStepInfo);
  const followingStepTypes: Array<LookupStepType> = [];

  if (isContainerStep) {
    if (isBoxContainerStep) {
      followingStepTypes.push(LookupStepType.BOX);
    }
    if (isPlateContainerStep) {
      followingStepTypes.push(LookupStepType.PLATE);
    }
    followingStepTypes.push(LookupStepType.CONTENTS);
  }
  if (isEntityStep) {
    const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
    const schema = lookupStepInfoWithType?.linkSchema;
    // If a user is selecting an entity as a run schema field with the first lookup step, the schema
    // will not be null and we can check if it's containable
    const isContainable = schema && (schema as TagSchema).isContainable;
    // If a user is in an intermediate step, they can be looking up any containable entity,
    // in which case the schema will be null, but we will want the CONTAINER step as an option.
    if (isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE}) && (isContainable || schema == null)) {
      followingStepTypes.push(LookupStepType.CONTAINER);
    }
    if (BenchlingConfig.REGISTRY_ENABLE_CHILD_ENTITIES) {
      followingStepTypes.push(LookupStepType.CHILD_ENTITIES);
    }
    if (isAaSequenceStep) {
      followingStepTypes.push(LookupStepType.AA_PART_LINKS_TO_ENTITY);
      followingStepTypes.push(LookupStepType.TRANSLATION_LINKS_TO_ENTITY);
    }
    if (isSequenceStep) {
      followingStepTypes.push(LookupStepType.PART_LINKS_TO_ENTITY);
    }
  }
  if (isPlateStep) {
    followingStepTypes.push(LookupStepType.WELLS);
  }
  if (isBoxStep) {
    followingStepTypes.push(LookupStepType.BOX_CONTAINERS);
  }
  if (isProcessTaskStep) {
    followingStepTypes.push(LookupStepType.FLOWCHART_PROCESS_TASK);
  }
  if (isContainerStep || isPlateStep || isBoxStep || isLocationStep) {
    followingStepTypes.push(LookupStepType.LOCATION);
  }
  if (
    (isContainerStep || isPlateStep || isBoxStep || isEntityStep) &&
    isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE})
  ) {
    followingStepTypes.push(LookupStepType.RESULTS);
  }
  return followingStepTypes.filter((stepType) => allowedLookupStepTypes.includes(stepType));
}

export function getConstantReplicationFactor(replicatesStep: ReplicatesLookupStepSpec): number | null {
  if (
    replicatesStep.numberLookupConfig.lookupSteps[0].type === LookupStepType.CONSTANT &&
    typeof replicatesStep.numberLookupConfig.lookupSteps[0].value === 'number'
  ) {
    return replicatesStep.numberLookupConfig.lookupSteps[0].value;
  }
  return null;
}

export function getLookupInfoArrayForLookupConfig(stepInfos: Array<LookupStepInfo>): Array<LookupStepInfo> {
  const lookupStepInfos = [];
  for (const lookupStepInfo of stepInfos) {
    lookupStepInfos.push(lookupStepInfo);
    if (lookupStepInfo.modifierLookupSteps) {
      lookupStepInfos.push(...lookupStepInfo.modifierLookupSteps);
    }
    if (lookupStepInfo.filterLookupSteps) {
      lookupStepInfos.push(...lookupStepInfo.filterLookupSteps);
    }
    if (lookupStepInfo.collapsingLookupSteps) {
      lookupStepInfos.push(...lookupStepInfo.collapsingLookupSteps);
    }
  }
  return lookupStepInfos;
}

export function getStepDisplayInfo(lookupStepInfo: LookupStepInfo): {
  name: string;
  isArchived?: boolean;
  tooltip?: string | null;
  isDeleted?: boolean;
} {
  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
  const lookupStep = lookupStepInfoWithType ? lookupStepInfoWithType.lookupStep : lookupStepInfo.lookupStep;
  const schema = lookupStepInfoWithType?.linkSchema;
  switch (lookupStep.type) {
    case LookupStepType.SAMPLE_OWNERS:
    case LookupStepType.AUTHORIZED_USERS:
      return {name: `${lookupStepInfo.name}`};
    case LookupStepType.COUNT: {
      const schema = lookupStepInfo.parentSchema;
      if (schema) {
        const schemaField = schema.allFields.find(
          (field) => field.apiId === lookupStep.schemaField || field.systemName === lookupStep.schemaField
        );
        if (schemaField) {
          const schemaFieldName = getArchiveItemName(getSchemaFieldArchivableItem(schemaField));
          return {
            name: `Count (${schemaFieldName})`,
            isArchived: !lookupStepInfo.shouldSkipValidation && isSchemaFieldArchived(schemaField),
          };
        }
      }
      return {name: 'Count', isDeleted: !lookupStepInfo.shouldSkipValidation};
    }
    case LookupStepType.CONSTANT:
      return {name: `${lookupStep.value}`};
    case LookupStepType.CONCENTRATION:
      return {name: `${lookupStepInfo.name} (${lookupStep.concentrationUnits})`};
    case LookupStepType.QUANTITY:
      return {name: `${lookupStepInfo.name} (${lookupStep.volumeUnits})`};
    case LookupStepType.AA_PART_LINKS_TO_ENTITY:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any AA Sequence',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.BOX:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Box',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.CHILD_ENTITIES:
      return {
        name: schema ? getArchiveItemName(schema) : DEFAULT_ENTITY_SCHEMA_OPTION_NAME,
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.CONTAINER:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Container',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.CONTENTS:
      return {
        name: schema
          ? getArchiveItemName(schema)
          : lookupStep.entitySchemaType
            ? 'Any '.concat(getTagSchemaTypeName({folderItemType: lookupStep.entitySchemaType}))
            : DEFAULT_ENTITY_SCHEMA_OPTION_NAME,
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.LOCATION:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Location',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.PART_LINKS_TO_ENTITY:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Sequence',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.PLATE:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Plate',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.RESULTS:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Result',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.SCHEMA_FIELD:
      return {
        name: getArchiveItemName({
          ...lookupStepInfo.schemaField,
          id: lookupStepInfo.schemaField?.apiId ?? '',
          name:
            schema && lookupStepInfo.schemaField
              ? `${lookupStepInfo.schemaField.displayName} (${schema.name})`
              : lookupStepInfo.name,
        }),
        isArchived:
          !lookupStepInfo.shouldSkipValidation && lookupStepInfo.schemaField
            ? isSchemaFieldArchived(lookupStepInfo.schemaField)
            : false,
        tooltip: schema && lookupStepInfo.schemaField ? 'Field name (Linked schema)' : null,
        isDeleted: !lookupStepInfo.shouldSkipValidation && lookupStepInfo.schemaField == null,
      };
    case LookupStepType.FILTER_BY_ORIGIN:
      return {
        name: schema ? getArchiveItemName(schema) : 'Filter By Origin',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.CONTAINER_BY_GRID_POSITION: {
      const defaultLabel = 'Well/container';
      const name = lookupStepInfo.schemaField
        ? getPlateOrBoxContainerLabel(lookupStepInfo.schemaField, defaultLabel, lookupStepInfo.linkSchema)
        : defaultLabel;
      return {
        name,
        isArchived:
          !lookupStepInfo.shouldSkipValidation && lookupStepInfo.schemaField
            ? isSchemaFieldArchived(lookupStepInfo.schemaField)
            : false,
        tooltip: lookupStepInfo.name,
        isDeleted: !lookupStepInfo.shouldSkipValidation && lookupStepInfo.schemaField == null,
      };
    }
    case LookupStepType.FLOWCHART_PROCESS_TASK:
      if (BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG) {
        return {
          name: 'Start',
          isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
        };
      }
      return {
        name: 'Flowchart task',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.PROCESS_TASK:
      return {
        name: 'Task',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    case LookupStepType.STUDIES:
      return {name: 'Studies'};
    case LookupStepType.TRANSLATION_LINKS_TO_ENTITY:
      return {
        name: schema ? getArchiveItemName(schema) : 'Any Sequence',
        isArchived: !lookupStepInfo.shouldSkipValidation && schema ? isArchived(schema) : false,
      };
    default:
      return {name: lookupStepInfo.name};
  }
}

export type RunLookupConfigModalProps = MakeFieldsOptional<
  LookupConfigModalPropsWithoutExtraState,
  'onRequestSubmit' | 'onRequestClose'
>;

export function getItemStringForLookupStepType(
  lookupStepType?: LookupStepType,
  isPlural: boolean = false
): string {
  let itemName = '';
  switch (lookupStepType) {
    case LookupStepType.CONTENTS:
      itemName = 'content';
      break;
    case LookupStepType.RESULTS:
      itemName = 'result';
      break;
    default:
      itemName = 'item';
      break;
  }
  return pluralize(isPlural ? 2 : 1, itemName);
}

export function getItemTypeForResultsStep(lastLookupStep: LookupStepInfo): string {
  const {isEntityStep, isContainerStep, isPlateStep, isBoxStep} =
    getObjectTypesForLookupStepInfo(lastLookupStep);
  const applicableStorableSteps = [isContainerStep, isPlateStep, isBoxStep].filter(
    (isStepType) => isStepType
  );
  assert(
    isEntityStep || isContainerStep || isPlateStep || isBoxStep,
    'Expected step before results step to be an entity or storable step.'
  );
  if (isEntityStep) {
    return 'entity';
  }
  if (applicableStorableSteps.length > 1) {
    return 'inventory item';
  }
  if (isContainerStep) {
    return 'container';
  }
  if (isPlateStep) {
    return 'plate';
  }
  return 'box';
}

export function mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(
  schemaInterfaceGraphQL: SchemaInterfaceWithFieldsGraphQL
): SchemaInterfaceExtendingSchema {
  const allFields: Array<SchemaField> = schemaInterfaceGraphQL.fields.map((field) => ({
    id: field.id,
    dbId: field.id,
    apiId: field.apiId,
    name: field.displayName,
    systemName: field.systemName,
    displayName: field.displayName,
    fieldType: field.fieldType as FieldType,
    isRequired: field.isRequired,
    isMulti: field.isMulti,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isParentLink: false,
    requiredLink: field.requiredLinkJson,
    schemaFieldSelectorId: field.schemaFieldSelectorId,
    archiveRecord: null,
  }));

  const baseBioentityType: BaseBioentityType | null = schemaInterfaceGraphQL.baseBioentityType
    ? {
        folderItemType: schemaInterfaceGraphQL.baseBioentityType
          .folderItemType as TagSchemaSupportedFolderItemType,
        sequenceType: schemaInterfaceGraphQL.baseBioentityType
          .sequenceType as TagSchemaSequenceSchemaType | null,
      }
    : null;

  return {
    id: schemaInterfaceGraphQL.id,
    name: schemaInterfaceGraphQL.name,
    baseBioentityType,
    allFields,
    fields: allFields,
    archiveRecord: schemaInterfaceGraphQL.archiveRecord,
  };
}

export function getRequiredLinkSchema(field: SchemaField): Schema | null {
  const requiredLinkSchemaId = getRequiredLinkSchemaId(field);
  if (requiredLinkSchemaId == null) {
    return null;
  }
  if (APIUtil.isTagSchema(requiredLinkSchemaId) || APIUtil.isSchemaInterface(requiredLinkSchemaId)) {
    return getTagSchemaOrSchemaInterfaceFromCache(requiredLinkSchemaId);
  }
  if (APIUtil.isAssaySchema(requiredLinkSchemaId)) {
    return getAssaySchemaFromCache(requiredLinkSchemaId);
  }
  assert(APIUtil.isStorableSchema(requiredLinkSchemaId), `Unexpected schema id ${requiredLinkSchemaId}`);
  return getStorableSchemaFromCache(requiredLinkSchemaId);
}

export function getLinkSchemaForFieldStep(
  lookupStep: SchemaFieldLookupStepSpec | ContainerByGridPositionLookupStepSpec,
  parentSchema: Schema | null
): Schema | null {
  if (isStepWithTypecastSchemaMetadata(lookupStep) && lookupStep.typecastSchema) {
    return getTagSchemaOrSchemaInterfaceFromCache(lookupStep.typecastSchema);
  }
  if (parentSchema != null) {
    const field: SchemaField | undefined = parentSchema.allFields.find(
      ({apiId, systemName}) => apiId === lookupStep.schemaField || systemName === lookupStep.schemaField
    );
    return field ? getRequiredLinkSchema(field) : null;
  }
  return null;
}

export function getLinkSchemaForLookupStep(
  lookupStep: LookupStepSpec,
  parentSchema: Schema | null
): Schema | null {
  switch (lookupStep.type) {
    case LookupStepType.SCHEMA_FIELD: {
      return getLinkSchemaForFieldStep(lookupStep, parentSchema);
    }
    case LookupStepType.BOX:
    case LookupStepType.PLACEHOLDER_BOXES:
      return lookupStep.boxSchema ? getBoxSchemaFromCache(lookupStep.boxSchema) : null;
    case LookupStepType.BOX_CONTAINERS:
    case LookupStepType.WELLS: {
      if (parentSchema == null) {
        return null;
      }
      const nextSchemaId: string | null = (parentSchema as GridSchema).containerSchemaId ?? null;
      return nextSchemaId ? getContainerSchemaFromCache(nextSchemaId) : null;
    }
    case LookupStepType.AA_PART_LINKS_TO_ENTITY:
    case LookupStepType.CHILD_ENTITIES:
    case LookupStepType.CONTENTS:
    case LookupStepType.PART_LINKS_TO_ENTITY:
    case LookupStepType.SCHEMA_IDENTITY:
    case LookupStepType.TRANSLATION_LINKS_TO_ENTITY:
      return lookupStep.entitySchema ? getTagSchemaOrSchemaInterfaceFromCache(lookupStep.entitySchema) : null;
    case LookupStepType.PLATE:
    case LookupStepType.PLACEHOLDER_PLATES:
      return lookupStep.plateSchema ? getPlateSchemaFromCache(lookupStep.plateSchema) : null;
    case LookupStepType.LOCATION:
      return lookupStep.locationSchema ? getLocationSchemaFromCache(lookupStep.locationSchema) : null;
    case LookupStepType.CONTAINER:
      return lookupStep.containerSchema ? getContainerSchemaFromCache(lookupStep.containerSchema) : null;
    case LookupStepType.CONTAINER_BY_GRID_POSITION: {
      const gridSchema = getLinkSchemaForFieldStep(lookupStep, parentSchema) as GridSchema | null;
      const nextSchemaId: string | null = gridSchema?.containerSchemaId ?? null;
      return nextSchemaId ? getContainerSchemaFromCache(nextSchemaId) : null;
    }
    case LookupStepType.RESULTS:
      if (lookupStep.linkedBySchemaField == null) {
        return null;
      }
      for (const schema of getAllResultSchemasFromCache()) {
        if (schema == null) {
          continue;
        }
        for (const field of schema.allFields) {
          if (lookupStep.linkedBySchemaField === field.apiId) {
            return schema;
          }
        }
      }
      return null;
    case LookupStepType.TASK_FINDER_PROCESS_TASKS:
      if (lookupStep.linkedBySchemaField == null) {
        return null;
      }
      for (const schema of getAllProcessTaskSchemasFromCache()) {
        if (schema == null) {
          continue;
        }
        for (const field of schema.allFields) {
          if (lookupStep.linkedBySchemaField === field.apiId) {
            return schema;
          }
        }
      }
      return null;
    case LookupStepType.FLOWCHART_PROCESS_TASK:
      return getProcessTaskSchemaFromCache(lookupStep.processTaskSchema);
    case LookupStepType.PROCESS_TASK: {
      if (parentSchema == null) {
        return null;
      }
      return (
        getAllProcessTaskSchemasFromCache().find(
          (schema: Schema) => (schema as ProcessTaskSchemaGraphQL).outputSchema?.id === parentSchema.id
        ) ?? null
      );
    }
    case LookupStepType.NOTEBOOK_COLUMN_TYPECHECK: {
      if (ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES.includes(lookupStep.notebookColumnItemType)) {
        return lookupStep.notebookColumnStorableSchema
          ? getStorableSchemaFromCache(lookupStep.notebookColumnStorableSchema)
          : null;
      }
      return lookupStep.notebookColumnEntitySchema
        ? getTagSchemaOrSchemaInterfaceFromCache(lookupStep.notebookColumnEntitySchema)
        : null;
    }
    default:
      return null;
  }
}
